import type { EventReanalysisContext, TaxonomyClass } from "@sitescene/types";

import { EventReanalysisKind, NONE_CLASS_ID } from "@sitescene/types";

/** How the pages name `none` wherever a class would go. */
export const NOTHING_ALERTABLE = "Nothing alertable";

/** The class staff chose, as `deriveReanalysisKind` needs it and as the page names it. */
export interface ReanalysisTarget {
	alertable: boolean;
	classId: string;
	name: string;
}

type ClassOption = Pick<TaxonomyClass, "alertable" | "classId" | "displayName">;

/** Display names by class id, for records that carry only the id. */
export const classNames = (
	classes: readonly ClassOption[] | undefined,
): Map<string, string> =>
	new Map((classes ?? []).map((c) => [c.classId, c.displayName]));

/** A class id as staff read it: its display name when the taxonomy knows it. */
export const nameClass = (
	classId: string,
	names: ReadonlyMap<string, string>,
): string =>
	classId === NONE_CLASS_ID
		? NOTHING_ALERTABLE
		: (names.get(classId) ?? classId);

/**
 * The dropdown's choice as a target. Undefined until one is chosen, or when
 * the taxonomy no longer has it.
 */
export const resolveTarget = (
	classId: string,
	classes: readonly ClassOption[],
): ReanalysisTarget | undefined => {
	if (classId === NONE_CLASS_ID) {
		return { alertable: false, classId, name: NOTHING_ALERTABLE };
	}
	const match = classes.find((c) => c.classId === classId);
	return match
		? { alertable: match.alertable, classId, name: match.displayName }
		: undefined;
};

/**
 * What Submit will test, in plain words, for the case `deriveReanalysisKind`
 * derived from the chosen class and the event as it stands — or, when it
 * derived none, why there is nothing to correct. The null wording follows the
 * helper's two null branches rather than re-deriving them, so it needs no
 * alert line of its own.
 */
export const describeReanalysis = ({
	context,
	kind,
	target,
}: {
	context: Pick<EventReanalysisContext, "currentClassId" | "currentClassName">;
	kind: EventReanalysisKind | null;
	target: ReanalysisTarget;
}): string => {
	const current =
		context.currentClassName ??
		context.currentClassId ??
		"the event's current class";

	switch (kind) {
		case EventReanalysisKind.FalseAlarm:
			return target.classId === NONE_CLASS_ID
				? `This will re-test ${current} against the footage. If the footage doesn't support ${current}, the event is lowered.`
				: `This will re-test ${current} with ${target.name} as the likely look-alike. If the footage doesn't support ${current}, the event is lowered.`;
		case EventReanalysisKind.Misclassified:
			return `This will test whether the footage supports ${target.name} instead of ${current}. If it does, the event is relabeled and keeps its priority.`;
		case EventReanalysisKind.Missed:
			return `This will test whether the footage shows ${target.name}. If it does, the event is raised to an alert.`;
		default:
			// Nothing alertable on an event that never alerted, or the very
			// class the alert already names.
			if (target.classId === NONE_CLASS_ID) {
				return "This event is already below the alert line, so there is nothing to correct.";
			}
			if (!target.alertable) {
				return `This event is already below the alert line, and ${target.name} is not something that alerts, so there is nothing to correct.`;
			}
			return `This alert already names ${target.name}, so there is nothing to correct.`;
	}
};
